/**
 * Constellation — Tier 3 ambient (Animation Spec §5.1).
 *
 * Canvas-based starfield with slow twinkle and optional
 * connecting edges. Pauses when:
 *   - the document is hidden (Page Visibility API, spec §7), or
 *   - prefers-reduced-motion is set (spec §8) — renders a single static frame.
 *
 * Ported and tightened from `_prototype/js/constellation.jsx`.
 */

import { useEffect, useRef } from 'react';

interface ConstellationProps {
  density?: number;
  drawLines?: boolean;
  seed?: number;
  glow?: boolean;
  className?: string;
}

interface Star {
  x: number;
  y: number;
  r: number;
  a: number;
  twinkle: boolean;
  tDur: number;
  tDelay: number;
  appear: number;
  isBlue: boolean;
}

interface Edge {
  a: number;
  b: number;
  drawAt: number;
  dur: number;
}

export function Constellation({
  density = 70,
  drawLines = false,
  seed = 3,
  glow = true,
  className,
}: ConstellationProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let stars: Star[] = [];
    let edges: Edge[] = [];
    let raf = 0;
    let start = performance.now();
    let mounted = true;
    let paused = false;

    // Seeded RNG so layout is stable per `seed` prop.
    let s = seed * 9301 + 49297;
    const rand = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };

    function resize() {
      const r = canvas!.getBoundingClientRect();
      canvas!.width = r.width * dpr;
      canvas!.height = r.height * dpr;
      ctx!.setTransform(1, 0, 0, 1, 0, 0);
      ctx!.scale(dpr, dpr);
      generate(r.width, r.height);
    }

    function generate(w: number, h: number) {
      stars = [];
      for (let i = 0; i < density; i++) {
        const isLarge = rand() > 0.85;
        const isBlue = rand() < 0.18;
        stars.push({
          x: rand() * w,
          y: rand() * h,
          r: isLarge ? 1.2 + rand() * 1.6 : 0.4 + rand() * 1.0,
          a: 0.05 + rand() * 0.3,
          twinkle: rand() < 0.32,
          tDur: 2 + rand() * 5,
          tDelay: rand() * 6,
          appear: 0.2 + rand() * 1.6,
          isBlue,
        });
      }
      edges = [];
      if (drawLines) {
        const limit = 60;
        for (let i = 0; i < stars.length; i++) {
          let count = 0;
          for (let j = i + 1; j < stars.length; j++) {
            const a = stars[i];
            const b = stars[j];
            if (!a || !b) continue;
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d < limit && count < 2) {
              edges.push({ a: i, b: j, drawAt: 1.4 + rand() * 2.2, dur: 0.9 + rand() * 0.8 });
              count++;
            }
          }
        }
      }
    }

    function tick(now: number) {
      if (!mounted) return;
      if (paused) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const t = (now - start) / 1000;
      const r = canvas!.getBoundingClientRect();
      ctx!.clearRect(0, 0, r.width, r.height);

      if (glow) {
        const g1 = ctx!.createRadialGradient(
          r.width * 0.18,
          r.height * 0.25,
          0,
          r.width * 0.18,
          r.height * 0.25,
          360
        );
        g1.addColorStop(0, 'rgba(45,127,255,0.13)');
        g1.addColorStop(1, 'rgba(45,127,255,0)');
        ctx!.fillStyle = g1;
        ctx!.fillRect(0, 0, r.width, r.height);

        const g2 = ctx!.createRadialGradient(
          r.width * 0.78,
          r.height * 0.7,
          0,
          r.width * 0.78,
          r.height * 0.7,
          320
        );
        g2.addColorStop(0, 'rgba(155,126,200,0.06)');
        g2.addColorStop(1, 'rgba(155,126,200,0)');
        ctx!.fillStyle = g2;
        ctx!.fillRect(0, 0, r.width, r.height);
      }

      if (drawLines) {
        for (const e of edges) {
          const A = stars[e.a];
          const B = stars[e.b];
          if (!A || !B) continue;
          const localT = (t - e.drawAt) / e.dur;
          if (localT <= 0) continue;
          const k = Math.min(1, localT);
          const ex = A.x + (B.x - A.x) * k;
          const ey = A.y + (B.y - A.y) * k;
          ctx!.strokeStyle = `rgba(45,127,255,${0.15 * Math.min(1, localT)})`;
          ctx!.lineWidth = 0.5;
          ctx!.beginPath();
          ctx!.moveTo(A.x, A.y);
          ctx!.lineTo(ex, ey);
          ctx!.stroke();
        }
      }

      for (const st of stars) {
        const appearK = Math.min(1, Math.max(0, (t - st.appear) / 1.0));
        let alpha = st.a * appearK;
        if (st.twinkle) {
          const ph = (Math.sin(((t + st.tDelay) / st.tDur) * Math.PI * 2) + 1) / 2;
          alpha = (st.a * 0.6 + st.a * 1.6 * ph) * appearK;
        }
        if (alpha < 0.005) continue;
        if (st.isBlue) {
          ctx!.fillStyle = `rgba(45,127,255,${alpha * 0.35})`;
          ctx!.beginPath();
          ctx!.arc(st.x, st.y, st.r * 3, 0, Math.PI * 2);
          ctx!.fill();
          ctx!.fillStyle = `rgba(120,170,255,${alpha})`;
        } else {
          ctx!.fillStyle = `rgba(236,238,248,${alpha})`;
        }
        ctx!.beginPath();
        ctx!.arc(st.x, st.y, st.r, 0, Math.PI * 2);
        ctx!.fill();
      }

      raf = requestAnimationFrame(tick);
    }

    function renderStaticFrame() {
      // Single resolved frame — for prefers-reduced-motion. Skips animation,
      // composes stars at their settled alpha so the bg still has texture.
      const r = canvas!.getBoundingClientRect();
      ctx!.clearRect(0, 0, r.width, r.height);

      if (glow) {
        const g1 = ctx!.createRadialGradient(
          r.width * 0.18,
          r.height * 0.25,
          0,
          r.width * 0.18,
          r.height * 0.25,
          360
        );
        g1.addColorStop(0, 'rgba(45,127,255,0.10)');
        g1.addColorStop(1, 'rgba(45,127,255,0)');
        ctx!.fillStyle = g1;
        ctx!.fillRect(0, 0, r.width, r.height);
      }
      for (const st of stars) {
        const alpha = st.a;
        if (st.isBlue) {
          ctx!.fillStyle = `rgba(45,127,255,${alpha * 0.35})`;
          ctx!.beginPath();
          ctx!.arc(st.x, st.y, st.r * 3, 0, Math.PI * 2);
          ctx!.fill();
          ctx!.fillStyle = `rgba(120,170,255,${alpha})`;
        } else {
          ctx!.fillStyle = `rgba(236,238,248,${alpha})`;
        }
        ctx!.beginPath();
        ctx!.arc(st.x, st.y, st.r, 0, Math.PI * 2);
        ctx!.fill();
      }
    }

    resize();

    if (reduced) {
      renderStaticFrame();
      return () => {
        mounted = false;
      };
    }

    raf = requestAnimationFrame(tick);

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const onVisibilityChange = () => {
      paused = document.hidden;
      // When un-pausing, advance `start` so the time delta stays smooth.
      if (!paused) start = performance.now() - (performance.now() - start);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [density, drawLines, glow, seed]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
}
